import {
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  IS_HIDE_SESSION_UPGRADE_BUTTON_EVIDENCE_FAKE_TRIALS_ENABLED,
} from '@/app-shell/flags/evidence-flags';
import { usePaywallStates } from '@/atoms/paywallAtoms';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isOnPrem } from '@/utils/buildProfile';

import { useIsHeidi2OptInEnabled } from '@/app-shell/flags/agents-flags';
import { IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED } from '@/app-shell/flags/commerce-flags';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { trialToPaidModalOpened } from '@heidi/analytics/events';
import {
  getReverseTrialCohort,
  isClinicianProductTier,
  isHeidi2OptInCreditUsageEnabled,
} from '@heidi/commerce';
import {
  paywallModalOpenFromSessionHeader,
  paywallModalOpenFromSidebar,
  useIsFreeUser,
  useReverseTrial,
} from '@heidi/commerce/data';

import { SignupSource, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

export type CTALocation = 'header' | 'sidebar' | 'none';

/** Where the CTA is allowed to render. Kept a constant to mirror the original
 * inline logic — the button is only ever surfaced in the `header` position. */
const CTA_POSITION: CTALocation = 'header';

const messages = defineMessages({
  practiceTrialRemaining: {
    defaultMessage: 'Time remaining on your Practice trial',
    id: 'eApRX+bRvQ',
    description: 'Tooltip describing the remaining time in a Practice trial',
  },
  clinicianTrialRemaining: {
    defaultMessage: 'Time remaining on your Clinician trial',
    id: 'X6N2cUfG8h',
    description: 'Tooltip describing the remaining time in a Clinician trial',
  },
  scribePlusTrialRemaining: {
    defaultMessage: 'Time remaining on your Scribe Plus trial',
    id: 'qkRYiCUYPS',
    description: 'Tooltip describing the remaining time in a Scribe Plus trial',
  },
  upgradeToClinician: {
    defaultMessage: 'Upgrade to Clinician',
    id: 'LfV/gcAL7s',
    description: 'Call to action to upgrade to the Clinician plan',
  },
  upgradeToPractice: {
    defaultMessage: 'Upgrade to Practice',
    id: 'p41RF+9pPo',
    description: 'Call to action to upgrade to the Practice plan',
  },
  upgradeNow: {
    defaultMessage: 'Upgrade now',
    id: 'iVMKenLdYh',
    description: 'Urgent call to action shown when a trial is ending',
  },
  hoursRemaining: {
    defaultMessage: '{count, plural, one {# hour} other {# hours}}',
    id: 'gw2a6zlf4C',
    description: 'Trial countdown label showing the number of hours remaining',
  },
  daysRemaining: {
    defaultMessage: '{count, plural, one {# day} other {# days}}',
    id: 'AZ2suEFo8a',
    description: 'Trial countdown label showing the number of days remaining',
  },
  unlockClinician: {
    defaultMessage: 'Unlock Clinician',
    id: 'z9wVyzuAA0',
    description: 'Call to action to unlock the Clinician plan',
  },
  tryClinicianForFree: {
    defaultMessage: 'Try Clinician for Free',
    id: 'zoAk45qGMk',
    description: 'Call to action to begin a free Clinician trial',
  },
  tryScribePlusForFree: {
    defaultMessage: 'Try Scribe Plus for free',
    id: 'My3jZ5837I',
    description: 'Call to action to begin a free Scribe Plus trial',
  },
  unlockHeidiPro: {
    defaultMessage: 'Unlock Heidi Pro',
    id: 'MfYGYYqNcy',
    description: 'Call to action to unlock the Heidi Pro plan',
  },
  upgradeToPro: {
    defaultMessage: 'Upgrade to Pro',
    id: '0LOgi8FM5+',
    description: 'Call to action to upgrade to the Pro plan',
  },
});

export type ResolvedTrialCountdownDisplay = {
  unit: 'days' | 'hours';
  value: number;
};

export function resolveTrialCountdownDisplay({
  isInReverseTrial,
  isFakeClinicianTrial,
  trialDaysLeft,
  trialHoursLeft,
  showHoursInsteadOfDays,
  clinicianTrialDaysLeft,
  clinicianTrialHoursLeft,
}: {
  isInReverseTrial: boolean;
  isFakeClinicianTrial: boolean;
  trialDaysLeft: number;
  trialHoursLeft: number;
  showHoursInsteadOfDays: boolean;
  clinicianTrialDaysLeft: number;
  clinicianTrialHoursLeft: number;
}): ResolvedTrialCountdownDisplay {
  if (isInReverseTrial) {
    return showHoursInsteadOfDays
      ? { unit: 'hours', value: trialHoursLeft }
      : { unit: 'days', value: trialDaysLeft };
  }

  return isFakeClinicianTrial && clinicianTrialDaysLeft === 0
    ? { unit: 'hours', value: clinicianTrialHoursLeft }
    : { unit: 'days', value: clinicianTrialDaysLeft };
}

export interface UpgradeCta {
  /** Whether the button should render at all (folds in the Apple-compliant /
   *  on-prem / position / eligibility guards). */
  shouldRender: boolean;
  /** The full V1 label, e.g. "Upgrade to Clinician" or a countdown "3 days". */
  buttonText: string;
  /**
   * True when `buttonText` is a trial countdown ("3 days" / "5 hours").
   * Presentation should branch on this — not on the label's content, which
   * is localized.
   */
  isCountdownLabel: boolean;
  /** Practice cohort → `pro_plus` styling + practice logo; otherwise `pro`. */
  isPracticeVariant: boolean;
  isInReverseTrial: boolean;
  /** In a reverse or fake-clinician trial, i.e. a live day/hour countdown. */
  inActiveTrial: boolean;
  /** Countdown unit + value behind a countdown `buttonText`. */
  countdownDisplay: ResolvedTrialCountdownDisplay;
  /**
   * Calendar days left in the active trial. Distinct from
   * `countdownDisplay` — that flips to hours near the end, this never does.
   */
  trialDaysRemaining: number;
  /** Cohort-aware "trial remaining" copy used by the V1 tooltip. */
  trialTooltipText: string;
  /**
   * Every branch of `handleClick` opens a Stripe-backed upgrade flow, so the CTA
   * is dead while offline. Presentation should disable the button and swap its
   * tooltip for the shared "Not available offline" copy.
   */
  isOfflineGated: boolean;
  /**
   * Heidi 2 opt-in users get the sidebar usage meter as their single upgrade
   * and trial surface — it mounts for every opt-in user on `heidi_version === 2`
   * (`useShouldRenderSidebarCreditUsagePopover`) and carries its own upgrade
   * CTA — so header surfaces stand down for the whole cohort, not just trials.
   * Kept separate from `shouldRender` so the baseline CTA eligibility (and the
   * nudge that derives from it) stays intact.
   */
  hidesHeaderUpgradeCta: boolean;
  handleClick: () => void;
}

/**
 * Single source of truth for the session upgrade CTA — eligibility, label,
 * tier variant, click routing, and tooltip copy.
 *
 * Extracted from `UpgradeCTAButton` so V1 (`upgrade-cta-button.tsx`) and the
 * V2 icon-only header button (`v2-upgrade-button.tsx`) share identical
 * business logic and only diverge on presentation.
 */
export function useUpgradeCta(location: CTALocation = 'header'): UpgradeCta {
  const intl = useIntl();
  const {
    toggleUpgradeProModal,
    toggleTrialToPaidModal,
    toggleClinicianUpgradeModal,
    togglePracticeUpgradeModal,
  } = usePaywallStates();
  const { shouldGateControls: isOfflineGated } = useOfflineGating();
  const { data: userData } = useQueryUser((data) => ({
    free_trial_history_tiers: data?.free_trial_history_tiers,
    signup_source: data?.signup_source,
  }));
  const {
    isInReverseTrial,
    isPracticeTrialUser,
    isEligibleForPracticeTrial,
    trialDaysLeft,
    trialHoursLeft,
    showHoursInsteadOfDays,
    isClinicianPracticeTrialUser,
    isClinicianTrialUser,
    isFakeClinicianTrial,
    clinicianTrialDaysLeft,
    clinicianTrialHoursLeft,
    clinicianTrialEnded,
    previousStatusBeforeClinicianTrial,
  } = useReverseTrial();
  const { hasTeam, isTeamAdmin, teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const { isFreeUser } = useIsFreeUser();
  const { userTier } = useQueryUserTier();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const isHeidi2OptInEnabled = useIsHeidi2OptInEnabled();
  const isEvidenceTier = billingInfo?.new_product_tier === SubscriptionTier.EVIDENCE;

  // Post–90-day-trial nudge: if a user does not upgrade to a clinician tier,
  // they should still see the upgrade button after their trial ends
  const showPostTrialCta =
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() &&
    clinicianTrialEnded &&
    !isClinicianProductTier(userTier);
  // Cohort decides the variant (Clinician → pro purple + flash logo,
  // Practice → pro_plus + practice logo) and the button label. Mirrors
  // the trial-ended modal's own cohort routing so both surfaces stay in
  // sync on what the user is being pitched.
  const wasClinicianPlan =
    getReverseTrialCohort({
      previousStatusBeforeClinicianTrial,
      userTier,
    }) === 'clinician';

  // can't do pro trial if user ever had a paid plan
  const freeTrialAvailable = useMemo(
    () => userData?.free_trial_history_tiers?.length === 0,
    [userData?.free_trial_history_tiers?.length]
  );
  const hasClinicianTierInFreeTrialHistory = useMemo(
    () =>
      userData?.free_trial_history_tiers?.some(
        (tier) =>
          tier === SubscriptionTier.CLINICIAN_PRO ||
          tier === SubscriptionTier.CLINICIAN_PRO_PLUS
      ) ?? false,
    [userData?.free_trial_history_tiers]
  );

  const clinicianFreeTrialAvailable = useMemo(
    () =>
      !hasClinicianTierInFreeTrialHistory &&
      !isClinicianPracticeTrialUser &&
      !isClinicianTrialUser,
    [
      hasClinicianTierInFreeTrialHistory,
      isClinicianPracticeTrialUser,
      isClinicianTrialUser,
    ]
  );
  const isLocatedSidebar = useMemo(() => location === 'sidebar', [location]);
  const showUpgradeButton = useMemo(() => {
    const shouldShowOnlyForRealClinicianTrials =
      IS_HIDE_SESSION_UPGRADE_BUTTON_EVIDENCE_FAKE_TRIALS_ENABLED()
        ? !isFakeClinicianTrial
        : true;
    const showForClinicianTrial =
      IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
      (isClinicianTrialUser || isClinicianPracticeTrialUser) &&
      shouldShowOnlyForRealClinicianTrials;
    return (
      (!hasTeam || (isTeamAdmin && teamDetails?.team.is_stripe_managed)) &&
      (isFreeUser || isInReverseTrial || showForClinicianTrial || showPostTrialCta)
    );
  }, [
    hasTeam,
    isFreeUser,
    isInReverseTrial,
    isClinicianTrialUser,
    isClinicianPracticeTrialUser,
    isTeamAdmin,
    teamDetails?.team.is_stripe_managed,
    isFakeClinicianTrial,
    showPostTrialCta,
  ]);

  // Practice cohort gets the pro_plus (purple practice) styling — used for
  // the variant prop AND the logo swap. Pre-extracting avoids repeating the
  // same disjunction inline twice.
  const isPracticeVariant = Boolean(
    isEligibleForPracticeTrial ||
    isClinicianPracticeTrialUser ||
    (showPostTrialCta && !wasClinicianPlan)
  );

  const handleClick = useCallback(() => {
    // Every branch below opens a billing modal that ends in Stripe, so offline
    // the click is a dead end — bail before any modal or analytics fires.
    if (isOfflineGated) {
      return;
    }

    if (showPostTrialCta) {
      // Route the post-trial cohort into the same generic upgrade modals
      // Free users land on today — cohort decides Clinician vs Practice
      // (mirrors the in-trial Evidence-launch branch below). The recap
      // modal (NINETY_DAY_TRIAL_END_MODAL) is intentionally NOT re-opened
      // here: it's the first-touch surface from the launch enqueue /
      // discreet card, and the header CTA exists to convert users who
      // already dismissed it with "Continue on current plan".
      if (wasClinicianPlan) {
        toggleClinicianUpgradeModal(true);
      } else {
        togglePracticeUpgradeModal(true);
      }
    } else if (
      IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
      userData?.signup_source !== SignupSource.scribe_plus
    ) {
      // For Evidence Pro trial users, open clinician upgrade modal
      if (isEvidenceTier) {
        toggleClinicianUpgradeModal(true);
      } else if (isClinicianPracticeTrialUser || isPracticeTrialUser) {
        togglePracticeUpgradeModal(true);
      } else {
        toggleClinicianUpgradeModal(true);
      }
    } else if (isInReverseTrial) {
      if (isPracticeTrialUser) {
        togglePracticeUpgradeModal(true);
      } else {
        trialToPaidModalOpened({
          location: 'cta-button',
        });
        toggleTrialToPaidModal(true);
      }
    } else {
      toggleUpgradeProModal(true);
    }

    if (isLocatedSidebar) {
      paywallModalOpenFromSidebar();
    } else {
      paywallModalOpenFromSessionHeader();
    }
  }, [
    isInReverseTrial,
    isPracticeTrialUser,
    isClinicianPracticeTrialUser,
    togglePracticeUpgradeModal,
    toggleTrialToPaidModal,
    toggleClinicianUpgradeModal,
    toggleUpgradeProModal,
    isLocatedSidebar,
    userData?.signup_source,
    isEvidenceTier,
    showPostTrialCta,
    wasClinicianPlan,
    isOfflineGated,
  ]);

  const trialTooltipText = useMemo(
    () =>
      isPracticeTrialUser || isClinicianPracticeTrialUser
        ? intl.formatMessage(messages.practiceTrialRemaining)
        : isClinicianTrialUser
          ? intl.formatMessage(messages.clinicianTrialRemaining)
          : intl.formatMessage(messages.scribePlusTrialRemaining),
    [intl, isPracticeTrialUser, isClinicianPracticeTrialUser, isClinicianTrialUser]
  );

  const inActiveTrial = isInReverseTrial || isFakeClinicianTrial;
  const trialDaysRemaining = isInReverseTrial ? trialDaysLeft : clinicianTrialDaysLeft;

  const countdownDisplay = useMemo(
    () =>
      resolveTrialCountdownDisplay({
        isInReverseTrial,
        isFakeClinicianTrial,
        trialDaysLeft,
        trialHoursLeft,
        showHoursInsteadOfDays: Boolean(showHoursInsteadOfDays),
        clinicianTrialDaysLeft,
        clinicianTrialHoursLeft,
      }),
    [
      isInReverseTrial,
      isFakeClinicianTrial,
      trialDaysLeft,
      trialHoursLeft,
      showHoursInsteadOfDays,
      clinicianTrialDaysLeft,
      clinicianTrialHoursLeft,
    ]
  );

  // Single memo produces the label AND whether it is a trial countdown, so
  // presentation code can branch on the semantic state instead of
  // regex-testing localized text (which breaks for locales that spell
  // numbers as words, or marketing copy containing a digit).
  const { buttonText, isCountdownLabel } = useMemo(() => {
    const countdown = (text: string) => ({ buttonText: text, isCountdownLabel: true });
    const label = (text: string) => ({ buttonText: text, isCountdownLabel: false });

    if (showPostTrialCta) {
      // Post-trial CTA: static "Upgrade to {tier}" — no countdown (the trial
      // is over). Cohort drives which tier we pitch, matching the recap modal.
      return label(
        wasClinicianPlan
          ? intl.formatMessage(messages.upgradeToClinician)
          : intl.formatMessage(messages.upgradeToPractice)
      );
    } else if (inActiveTrial) {
      const hoursLeft = isInReverseTrial ? trialHoursLeft : clinicianTrialHoursLeft;
      if (hoursLeft <= 1) {
        return label(intl.formatMessage(messages.upgradeNow));
      }
      return countdown(
        intl.formatMessage(
          countdownDisplay.unit === 'hours'
            ? messages.hoursRemaining
            : messages.daysRemaining,
          { count: countdownDisplay.value }
        )
      );
    } else if (
      IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
      userData?.signup_source !== SignupSource.scribe_plus
    ) {
      // Clinician flow: text must match handleClick (clinician trial modal vs upgrade modals)
      // For Evidence Pro trial users, show "Upgrade to Clinician"
      if (isEvidenceTier) {
        return label(
          isLocatedSidebar
            ? intl.formatMessage(messages.unlockClinician)
            : intl.formatMessage(messages.upgradeToClinician)
        );
      } else {
        return label(
          clinicianFreeTrialAvailable
            ? intl.formatMessage(messages.tryClinicianForFree)
            : isClinicianPracticeTrialUser
              ? intl.formatMessage(messages.upgradeToPractice)
              : isLocatedSidebar
                ? intl.formatMessage(messages.unlockClinician)
                : intl.formatMessage(messages.upgradeToClinician)
        );
      }
    } else if (freeTrialAvailable) {
      // PRO (legacy) flow
      return label(intl.formatMessage(messages.tryScribePlusForFree));
    } else {
      return label(
        isLocatedSidebar
          ? intl.formatMessage(messages.unlockHeidiPro)
          : intl.formatMessage(messages.upgradeToPro)
      );
    }
  }, [
    intl,
    showPostTrialCta,
    wasClinicianPlan,
    inActiveTrial,
    isInReverseTrial,
    trialHoursLeft,
    clinicianTrialHoursLeft,
    countdownDisplay,
    isEvidenceTier,
    isLocatedSidebar,
    clinicianFreeTrialAvailable,
    isClinicianPracticeTrialUser,
    freeTrialAvailable,
    userData?.signup_source,
  ]);

  // Boolean() collapses the `undefined` that `teamDetails?.team.is_stripe_managed`
  // can thread through `showUpgradeButton` (and `isPracticeVariant` via the same
  // optional chain) — the original inline guards tolerated truthiness, the typed
  // hook contract doesn't.
  const shouldRender = Boolean(
    !isAppleCompliantBuild() &&
    !isOnPrem &&
    CTA_POSITION === location &&
    showUpgradeButton
  );

  const hidesHeaderUpgradeCta = isHeidi2OptInCreditUsageEnabled({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });

  return {
    shouldRender,
    buttonText,
    isCountdownLabel,
    isPracticeVariant,
    isInReverseTrial,
    inActiveTrial,
    countdownDisplay,
    trialDaysRemaining,
    trialTooltipText,
    isOfflineGated,
    hidesHeaderUpgradeCta,
    handleClick,
  };
}
